import React, { createContext, useContext, ComponentProps } from 'react';
import clamp from 'lodash/clamp';
import useWindowScroll from 'react-use/lib/useWindowScroll';
import styled, { css } from 'styled-components';
import { IconButton } from '../input/IconButton';
import { Box } from '../layout/Box';
import { Flex, FlexProps } from '../layout/Flex';

const ToolbarContext = createContext<number>(0);

const ToolbarContainer = styled(Box)
    .withConfig({ displayName: 'ToolbarContainer' })
    .attrs((props) => ({
        ...props,
        position: props.position ?? { xs: 'sticky', md: 'relative' },
    }))`
    z-index: 10;
`;

interface ToolbarInnerProps {
    transparent: boolean;
}

const ToolbarInner = styled(Flex)
    .attrs<ToolbarInnerProps, FlexProps & ToolbarInnerProps>({
        zIndex: 100,
        position: 'relative',
        spacing: 1,
        width: '100%',
        direction: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    })
    .withConfig({
        displayName: 'ToolbarInner',
        shouldForwardProp: (prop) => !['transparent'].includes(prop),
    })`
    background-color: rgba(var(--background-primary-rgb), 0.65);
    -webkit-backdrop-filter: blur(--blur-radius-l);
    backdrop-filter: blur(var(--blur-radius-l));
    transition-property: background-color, backdrop-filter;
    transition-duration: 250ms;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);

    ${(props) =>
        props.transparent &&
        css`
            background-color: transparent;
            -webkit-backdrop-filter: none;
            backdrop-filter: none;
        `}
`;

interface ToolbarProps extends FlexProps {
    animationDistance?: number;
}

export const Toolbar = (props: ToolbarProps) => {
    const { position, children, animationDistance = 0, ...others } = props;
    const windowScroll = useWindowScroll();
    const portion = animationDistance > 0 ? clamp(windowScroll.y / animationDistance, 0, 1) : 1;

    return (
        <ToolbarContext.Provider value={portion}>
            <ToolbarContainer
                height={animationDistance > 0 ? 0 : 'auto'}
                position={position}
                top="var(--safe-area-inset-top)"
                {...others}
            >
                <ToolbarInner transparent={portion < 1}>{children}</ToolbarInner>
            </ToolbarContainer>
        </ToolbarContext.Provider>
    );
};

const Ripple = styled.div.withConfig({ displayName: 'Ripple' })`
    position: absolute;
    border-radius: 50%;
    width: 100%;
    height: 100%;
    background: rgba(var(--background-primary-rgb), 0.4);
    -webkit-backdrop-filter: blur(20px);
    backdrop-filter: blur(20px);
    will-change: transform, opacity;
    z-index: -1;
`;

export const ToolbarIconButton = (props: ComponentProps<typeof IconButton>) => {
    const { children, ...other } = props;
    const portion = useContext(ToolbarContext);

    return (
        <IconButton color="primary" {...other}>
            {portion < 1 && (
                <Ripple style={{ transform: `scale(${1 - portion / 4})`, opacity: 1 - portion }} />
            )}
            {children}
        </IconButton>
    );
};
